/**
 * AI Tutor Settings — voice picker for the student's own profile page.
 *
 * Selecting a voice saves it immediately (it's the tutor's TTS voice from
 * here on, any device) and plays a short sample right away so the choice is
 * never blind. Each row also has its own preview button to audition a voice
 * without committing to it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import {
  fetchTts,
  fetchVoice,
  fetchVoiceOptions,
  saveVoice,
  type VoiceOption,
} from '@components/pages/studentWorkSpace/tutor/api';
import { TUTOR_NAME } from '../../../../config/productNames';

const SAMPLE_TEXT = `Hi! I'm ${TUTOR_NAME}, your AI tutor. This is what I sound like — pick whichever voice feels right for you.`;

export default function AiTutorVoiceSettings() {
  const [options, setOptions] = useState<VoiceOption[]>([]);
  const [voice, setVoice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [{ options: opts, default: def }, current] = await Promise.all([
          fetchVoiceOptions(),
          fetchVoice().catch(() => null),
        ]);
        if (cancelled) return;
        setOptions(opts);
        setVoice(current ?? def);
      } catch {
        if (!cancelled) setNotice({ kind: 'error', text: `Could not load ${TUTOR_NAME} voices right now.` });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Cleanup: revoke any object URL and stop playback on unmount.
  useEffect(
    () => () => {
      audioRef.current?.pause();
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    },
    [],
  );

  const stopPlayback = useCallback(() => {
    audioRef.current?.pause();
    setPlayingId(null);
  }, []);

  const play = useCallback(async (voiceId: string) => {
    if (playingId === voiceId) {
      stopPlayback();
      return;
    }
    setPlayingId(voiceId);
    try {
      const buf = await fetchTts(SAMPLE_TEXT, voiceId);
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const url = URL.createObjectURL(new Blob([buf], { type: 'audio/mpeg' }));
      objectUrlRef.current = url;
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => setPlayingId((p) => (p === voiceId ? null : p));
      audio.onerror = () => setPlayingId((p) => (p === voiceId ? null : p));
      await audio.play();
    } catch {
      setPlayingId(null);
      setNotice({ kind: 'error', text: `Couldn't play that voice sample — ${TUTOR_NAME}'s voice service may be unavailable.` });
    }
  }, [playingId, stopPlayback]);

  const choose = useCallback(async (voiceId: string) => {
    const previous = voice;
    setVoice(voiceId);
    setSaving(true);
    setNotice(null);
    try {
      await saveVoice(voiceId);
      setNotice({ kind: 'success', text: `Saved — ${TUTOR_NAME} will use this voice from now on.` });
      void play(voiceId);
    } catch {
      setVoice(previous);
      setNotice({ kind: 'error', text: 'Could not save that voice — please try again.' });
    } finally {
      setSaving(false);
    }
  }, [voice, play]);

  return (
    <Card
      style={{ borderRadius: '10px', overflow: 'visible', alignSelf: 'stretch' }}
      sx={{ p: 4 }}
      data-testid="ai-tutor-voice-settings"
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
          <Typography variant="title">{TUTOR_NAME} Settings</Typography>
          {saving && <CircularProgress size={16} />}
        </Stack>
        <Typography variant="weak" sx={{ mb: 2, display: 'block' }}>
          Choose the voice {TUTOR_NAME} speaks with. Tap the play button to hear any voice before you pick it.
        </Typography>

        {notice && (
          <Alert severity={notice.kind} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice.text}
          </Alert>
        )}

        {loading ? (
          <CircularProgress size={22} />
        ) : (
          <RadioGroup
            value={voice ?? ''}
            onChange={(e) => void choose(e.target.value)}
          >
            <Stack spacing={1}>
              {options.map((opt) => (
                <Stack
                  key={opt.id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    p: 1,
                    border: '1px solid',
                    borderColor: voice === opt.id ? 'primary.main' : 'divider',
                    borderRadius: 2,
                  }}
                >
                  <FormControlLabel
                    value={opt.id}
                    control={<Radio disabled={saving} />}
                    sx={{ flex: 1, mr: 0 }}
                    label={
                      <Stack>
                        <Typography variant="subtitle2">{opt.label}</Typography>
                        <Typography variant="weak">{opt.hint}</Typography>
                      </Stack>
                    }
                  />
                  <IconButton
                    aria-label={`Play sample of ${opt.label}`}
                    onClick={() => void play(opt.id)}
                    size="small"
                  >
                    {playingId === opt.id ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </RadioGroup>
        )}
      </CardContent>
    </Card>
  );
}
